import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Heading from '@tiptap/extension-heading'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { generateJSON, generateHTML } from '@tiptap/html'
import { generateText } from '@tiptap/react'

export const getTiptapExtensions = (placeholder?: string) => [
    StarterKit.configure({
        heading: false,
    }),
    Heading.configure({
        levels: [1, 2, 3],
    }),
    Image.configure({
        HTMLAttributes: {
            class: 'rounded-box shadow-lg max-w-full h-auto my-4 mx-auto',
        },
    }),
    Link.configure({
        openOnClick: false,
        HTMLAttributes: {
            class: 'link link-primary',
        },
    }),
    ...(placeholder ? [
        Placeholder.configure({
            placeholder: placeholder,
            emptyEditorClass: 'is-editor-empty',
        })
    ] : []),
]

export const EDITOR_PROPS = {
    attributes: {
        class: 'prose prose-sm sm:prose-base dark:prose-invert max-w-none focus:outline-none min-h-[300px] p-6',
    },
}

export const RENDERER_PROPS = {
    attributes: {
        class: 'prose prose-sm sm:prose-base dark:prose-invert max-w-none focus:outline-none min-h-[10px]',
    },
}

export const SHARED_EDITOR_OPTIONS = {
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
}

/**
 * Utility to extract plain text from HTML content safely using Tiptap's utilities.
 * This ensures that the conversion follows the exact schema defined in the extensions.
 */
export const stripHtml = (html: string) => {
    if (!html) return ""

    try {
        const extensions = getTiptapExtensions()
        const json = generateJSON(html, extensions)
        return generateText(json, extensions).trim()
    } catch (e) {
        console.error('Error stripping HTML with Tiptap:', e)
        // Fallback to simple regex if Tiptap fails
        return html.replace(/<[^>]*>?/gm, '').trim()
    }
}

/**
 * Utility to normalize HTML content according to the Tiptap schema.
 * It parses the HTML and generates it back, effectively stripping 
 * unauthorized tags, attributes, and "cleaning" the markup.
 */
export const normalizeHtml = (html: string) => {
    if (!html) return ""

    try {
        const extensions = getTiptapExtensions()
        const json = generateJSON(html, extensions)
        return generateHTML(json, extensions)
    } catch (e) {
        console.error('Error normalizing HTML with Tiptap:', e)
        return html
    }
}
